import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);

  console.log('ProtectedRoute - authLoading:', authLoading, 'user:', user?.id);

  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        console.log('ProtectedRoute - No user, setting loading to false');
        setLoading(false);
        return;
      }

      // Only check admin status if requireAdmin is true
      if (requireAdmin) {
        console.log('ProtectedRoute - Checking admin status for user:', user.id);
        const { data, error } = await supabase
          .from('admin_users')
          .select('role')
          .eq('user_id', user.id)
          .single();

        if (!error && data) {
          console.log('ProtectedRoute - User is admin');
          setIsAdmin(true);
        } else {
          console.log('ProtectedRoute - User is not admin:', error);
          setIsAdmin(false);
        }
      } else {
        // For regular users, just set isAdmin to false (no need to check)
        console.log('ProtectedRoute - Regular user, not checking admin status');
        setIsAdmin(false);
      }
      setLoading(false);
    };

    checkAdminStatus();
  }, [user, requireAdmin]);

  console.log('ProtectedRoute - loading:', loading, 'isAdmin:', isAdmin);

  if (authLoading) {
    console.log('ProtectedRoute - Auth is loading, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    console.log('ProtectedRoute - Component is loading, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute - No user, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    console.log('ProtectedRoute - User is not admin, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('ProtectedRoute - User authenticated, rendering children');
  return <>{children}</>;
};

export default ProtectedRoute; 